import type { DigitalCancellationFormResponse } from '@eneco/dc-be/client';

const normalizePrefillValue = (value: string | null | undefined) => value?.trim() ?? '';

export const createCancelOrderPrefillValues = (customerData: DigitalCancellationFormResponse) => ({
  customerNumber: normalizePrefillValue(customerData.accountNumber),
  firstName: normalizePrefillValue(customerData.firstName),
  lastName: normalizePrefillValue(customerData.lastName),
  zipCode: normalizePrefillValue(customerData.address?.postalCode),
  city: normalizePrefillValue(customerData.address?.municipality),
  street: normalizePrefillValue(customerData.address?.street),
  houseNumber: normalizePrefillValue(customerData.address?.houseNumber),
  busNumber: normalizePrefillValue(customerData.address?.bus),
  email: normalizePrefillValue(customerData.email),
  phoneNumber: normalizePrefillValue(customerData.phoneNumber),
});

export type CancelOrderPrefillValues = ReturnType<typeof createCancelOrderPrefillValues>;

export type CancelOrderLockedFields = Record<keyof CancelOrderPrefillValues, boolean>;

/** Optional fields are never opened for manual completion: once prefilled they stay read-only, even when empty. */
const OPTIONAL_PREFILL_FIELDS: ReadonlySet<keyof CancelOrderPrefillValues> = new Set([
  'customerNumber',
  'firstName',
  'busNumber',
  'phoneNumber',
]);

/**
 * Required fields are locked (read-only) only when the backend returned a non-empty value, so missing
 * required data can be completed manually. Optional fields are always locked after prefill.
 */
export const createCancelOrderLockedFields = (prefillValues: CancelOrderPrefillValues): CancelOrderLockedFields =>
  Object.fromEntries(
    Object.entries(prefillValues).map(([fieldName, value]) => [
      fieldName,
      OPTIONAL_PREFILL_FIELDS.has(fieldName as keyof CancelOrderPrefillValues) || value !== '',
    ]),
  ) as CancelOrderLockedFields;
